// 本地相簿(APP#42)的 IndexedDB 持久層 —— 純本地、零後端。
//
// capture-to-local(APP#17)原本只觸發瀏覽器下載,無 in-app 留存;相簿需要一個
// 可瀏覽的本機來源,故在此新增一個 IndexedDB 物件庫,截圖 / 錄影落檔時同步存一份。
// 相簿頁 / 檢視器讀這裡。所有位元組都在瀏覽器端,永不上傳。
//
// 設計要點:
//   - store `items` keyPath=id,索引 createdAt(排序)與 deviceId(裝置過濾)。
//   - 清單用 listMeta() 只取中繼(不搬 blob),檢視 / 下載才用 getBlob() 取單筆位元組。
//   - 環境無 indexedDB(SSR / jsdom 測試)時全部優雅降級:寫入靜默 no-op、讀取回空。
//     → 既有 capture 測試(無 indexedDB)不受影響、相簿頁在不支援環境顯示空狀態。

import type { GalleryItem, GalleryKind, GalleryMeta } from './galleryLib';

const DB_NAME = 'ls-home-gallery';
const DB_VERSION = 1;
const STORE = 'items';

/** 此環境是否有 IndexedDB(jsdom / SSR 無)。 */
function hasIdb(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB != null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('createdAt', 'createdAt', { unique: false });
        os.createIndex('deviceId', 'deviceId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  }).catch((e) => {
    // 開啟失敗(隱私模式 / 配額)→ 讓後續呼叫重試,不永久卡住。
    dbPromise = null;
    throw e;
  });
  return dbPromise;
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
  });
}

/** 相簿是否可用(此瀏覽器支援 IndexedDB)。 */
export function galleryAvailable(): boolean {
  return hasIdb();
}

/**
 * addItem 結果:ok=已存、quota=容量不足(存不下,需告知使用者)、
 * unavailable=此環境無 IndexedDB、error=其他寫入失敗。皆不丟例外(截圖 / 錄影主流程照走)。
 */
export type AddResult = 'ok' | 'quota' | 'unavailable' | 'error';

/** 判斷是否為儲存配額超限(QuotaExceededError,各瀏覽器命名不一)。 */
function isQuotaError(e: unknown): boolean {
  return (
    typeof DOMException !== 'undefined' &&
    e instanceof DOMException &&
    (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || e.code === 22)
  );
}

/**
 * 新增一筆媒體到相簿。無 IndexedDB 時靜默略過(回 'unavailable')。
 * 容量不足回 'quota'(呼叫端據此提示使用者);其他失敗回 'error'。
 * 由 useMediaCapture 在截圖 / 錄影落檔時呼叫 —— 失敗皆不影響下載主流程。
 */
export async function addItem(item: GalleryItem): Promise<AddResult> {
  if (!hasIdb()) return 'unavailable';
  try {
    const db = await openDb();
    await reqToPromise(tx(db, 'readwrite').put(item));
    return 'ok';
  } catch (e) {
    // 相簿留存失敗不可影響截圖 / 錄影主流程(仍有下載);配額超限特別回報以便提示。
    return isQuotaError(e) ? 'quota' : 'error';
  }
}

/** 讀出全部中繼(不含 blob),由新到舊。無 IndexedDB 回空陣列。 */
export async function listMeta(): Promise<GalleryMeta[]> {
  if (!hasIdb()) return [];
  try {
    const db = await openDb();
    const all = await reqToPromise(tx(db, 'readonly').getAll() as IDBRequest<GalleryItem[]>);
    const metas = all.map(({ blob: _blob, ...meta }) => meta);
    return metas.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

/** 取單筆(含 blob),供檢視 / 下載。找不到回 null。 */
export async function getItem(id: string): Promise<GalleryItem | null> {
  if (!hasIdb()) return null;
  try {
    const db = await openDb();
    const item = await reqToPromise(tx(db, 'readonly').get(id) as IDBRequest<GalleryItem | undefined>);
    return item ?? null;
  } catch {
    return null;
  }
}

/** 刪一筆。無 IndexedDB 回 false。 */
export async function deleteItem(id: string): Promise<boolean> {
  if (!hasIdb()) return false;
  try {
    const db = await openDb();
    await reqToPromise(tx(db, 'readwrite').delete(id));
    return true;
  } catch {
    return false;
  }
}

/** 清空整個相簿。回刪除是否成功。 */
export async function clearAll(): Promise<boolean> {
  if (!hasIdb()) return false;
  try {
    const db = await openDb();
    await reqToPromise(tx(db, 'readwrite').clear());
    return true;
  } catch {
    return false;
  }
}

/** 產生相簿項目 id(有 crypto.randomUUID 用之,否則退回時間 + 隨機)。 */
export function newItemId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `g-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** 供測試重置已開啟的 DB 連線快取。 */
export function _resetDbCacheForTest(): void {
  dbPromise = null;
}

export type { GalleryItem, GalleryKind, GalleryMeta };
